import { useState, useContext, useEffect, useLayoutEffect, useMemo, useRef, useCallback, memo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Autocomplete from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import TerrainIcon from '@mui/icons-material/Terrain';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StraightenIcon from '@mui/icons-material/Straighten';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { useBranch } from '../../../contextApi/BranchContext';
import { usePermissions } from '../../../contextApi/PermissionContext';
import { saveMisPackingList } from '../../../store/store';
import SpecCodeBuilder from '../../inventory/specCodeBuilder';
import StoneNamesField from './stoneNamesField';
import { parseStoneCode } from '../../inventory/util/codeParser';
import MisDateField from '../misDateField';
import { DateSystemSelect, branchDocDefaults } from '../docSettings';
import { DraftCloseDialog, newDraftKey, MIS_DRAFT } from '../misDraftStrip';
import { normalizeDateSystem, toIsoDay, today } from '../../../tools/calendars';
import useFormDraft from '../../../tools/hooks/useFormDraft';
import useFlipList, { prefersReducedMotion } from '../../../tools/hooks/useFlipList';

const EMPTY_ITEM = { code: '', lengthCm: '', widthCm: '', thicknessCm: '', pcs: '', sqm: '', sqmTouched: false };

// ── The item grid ───────────────────────────────────────────────────────────
// ONE column definition, used by the header and by every row, so they cannot
// drift apart. It was two independent flex lines of fixed pixel widths before,
// which is how the numbers became unreadable: a flex child shrinks by default,
// so six 62px fields plus a 150px code in a drawer narrower than their sum were
// squeezed to slivers — on a phone down to a sliver that fits a caret and
// nothing else. `minmax(Npx, …)` cannot go below N; the code column takes the
// slack instead.
const ITEM_GRID = 'minmax(96px, 1.6fr) repeat(4, minmax(58px, 0.8fr)) minmax(76px, 1fr) 30px';

// A number input's spinner arrows eat ~17px INSIDE an already tight field and
// are useless for a measurement nobody nudges one at a time. inputMode still
// gives a phone the numeric keypad.
const NO_SPINNERS = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none', margin: 0,
  },
};
const EMPTY_PALLET = { palletId: '', reference: '', productCode: '', processingType: '', items: [{ ...EMPTY_ITEM }] };

// Every pallet row carries a `_key` that is its identity while the form is open (it is never sent to the server). The
// rows can be re-ordered, and a React key made of the row's POSITION would then hand one row's field state (the open
// processing-type box, the focused input) to whichever row slid into its place.
let palletKeySeq = 0;
const newPalletKey = () => { palletKeySeq += 1; return `pk${Date.now().toString(36)}-${palletKeySeq}`; };
const withKey = (p) => (p && p._key ? p : { ...p, _key: newPalletKey() });

// Common processing descriptions seen on the real packing lists. freeSolo —
// the typed text IS the stored value, so these are only suggestions.
const PROCESSING_SUGGESTIONS = ['FLD (tile)', 'UNFLD (slab)', 'CRSCT (tile)', 'VNCT (tile)', 'Blocks', 'Cut-to-size'];

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Every pallet of ONE list needs its own ID and its own reference (the server refuses a list otherwise).
// Case and extra spaces do not make two values different ("a 1" = "A  1"), an empty reference is "not given"
// rather than a duplicate of another empty one, and only rows that will really be saved take part: a row
// with no ID is dropped on save.
const palletNorm = (v) => String(v == null ? '' : v).trim().replace(/\s+/g, ' ').toLowerCase();

// -> { id: { [row]: [IDs of the other pallets it clashes with] }, ref: { [row]: [...] },
//      groups: [{ field: 'palletId' | 'reference', value, pallets: [IDs] }] }
function palletClashes(pallets) {
  const out = { id: {}, ref: {}, groups: [] };
  for (const [field, bucket] of [['palletId', 'id'], ['reference', 'ref']]) {
    const byValue = new Map();
    pallets.forEach((p, row) => {
      if (!palletNorm(p.palletId)) return;
      const key = palletNorm(p[field]);
      if (!key) return;
      if (!byValue.has(key)) byValue.set(key, []);
      byValue.get(key).push(row);
    });
    for (const rows of byValue.values()) {
      if (rows.length < 2) continue;
      const ids = rows.map((r) => String(pallets[r].palletId).trim());
      out.groups.push({ field, value: String(pallets[rows[0]][field]).trim(), pallets: ids });
      rows.forEach((r) => { out[bucket][r] = ids.filter((_, i) => rows[i] !== r); });
    }
  }
  return out;
}

// A default ID no pallet of the list already has (removing P2 of three and adding one must not hand out P3 twice).
function freePalletId(pallets) {
  const used = new Set(pallets.map((p) => palletNorm(p.palletId)));
  let n = pallets.length + 1;
  while (used.has(palletNorm(`P${n}`))) n += 1;
  return `P${n}`;
}
// ... and for a copy: P1-copy, then P1-copy2 ...
function copyPalletId(pallets, id) {
  const base = `${String(id || '').trim() || 'P'}-copy`;
  const used = new Set(pallets.map((p) => palletNorm(p.palletId)));
  if (!used.has(palletNorm(base))) return base;
  let n = 2;
  while (used.has(palletNorm(`${base}${n}`))) n += 1;
  return `${base}${n}`;
}

// IBAN is stored unspaced + uppercased, displayed grouped in fours.
const ibanRaw = (v) => String(v || '').replace(/[\s-]+/g, '').toUpperCase();
const ibanDisplay = (v) => ibanRaw(v).replace(/(.{4})/g, '$1 ').trim();

// Mirrors the server's productPrefix() in routes/mis/packingLists.js — a
// packing item's code is a NOMINAL product code (TR09), so scope is validated
// as a product-code prefix match, not a foreign key. Kept in sync so the form
// can warn before the save round-trips into a 400.
const productPrefix = (code) => {
  if (!code) return '';
  const m = String(code).toUpperCase().match(/^([A-Z]{2}\d{2})/);
  return m ? m[1] : String(code).toUpperCase();
};

// m² for one item row. Unsized slabs (L or W = 0) can't be derived, so those
// keep whatever was typed.
const deriveSqm = (it) => {
  const l = Number(it.lengthCm) || 0, w = Number(it.widthCm) || 0, p = Number(it.pcs) || 0;
  if (!l || !w || !p) return null;
  return round2((l / 100) * (w / 100) * p);
};

// A row nobody has filled in yet: no sizes / counts, and no code beyond the pallet's own.
const isBlankRow = (it, pallet) => !String(it.pcs || '') && !String(it.sqm || '')
  && !String(it.lengthCm || '') && !String(it.widthCm || '')
  && (!String(it.code || '').trim() || String(it.code).trim() === String(pallet.productCode || '').trim());

// The "No" box of a pallet row: its place in the list, shown as the row's number. Typing another number and leaving the
// box (or pressing Enter) moves the pallet there; 0, nothing or a non-number moves nothing; Escape drops the typed number
// (and does not close the form, as it would from any other field). It keeps the number being typed to ITSELF: held in the
// form, every keystroke would re-draw every pallet row - on a list of 30 that is the difference between typing and waiting.
const NO_FIELD = { size: 'small', InputLabelProps: { shrink: true } };
function PalletNoInput({ place, palletKey, index, label, help, onMove }) {
  const [text, setTextState] = useState(null);   // the number being typed; null = not editing, show the place
  const textRef = useRef(null);                  // ...also in a ref, so a blur that follows Enter cannot commit twice
  const setText = (v) => { textRef.current = v; setTextState(v); };
  const commit = () => {
    const typed = textRef.current;
    setText(null);
    if (typed == null) return;
    const to = parseInt(typed, 10);
    if (Number.isFinite(to) && to >= 1) onMove(palletKey, to);
  };
  return (
    <Tooltip title={help}>
      <TextField {...NO_FIELD} label={label} value={text == null ? String(place) : text}
        onFocus={(e) => { setText(String(place)); e.target.select(); }}
        onChange={(e) => setText(e.target.value.replace(/\D/g, '').slice(0, 4))}
        onBlur={commit}
        inputProps={{
          inputMode: 'numeric', 'aria-label': label, 'data-testid': `pl-pallet-no-${index}`, 'data-pallet-no': palletKey,
          // Enter commits and leaves the box - and, claiming the key, keeps the app-wide "Enter moves to the next field"
          // from also firing into a row that has just moved.
          enterKeyHint: 'done',
          onKeyDown: (e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); }
            else if (e.key === 'Escape' && textRef.current != null) { e.stopPropagation(); setText(null); e.currentTarget.blur(); }
          },
        }}
        sx={{ width: { xs: 68, sm: 62 }, '& input': { textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' } }} />
    </Tooltip>
  );
}

// Defined at MODULE scope on purpose. A component declared inside the form body
// is a NEW component type on every render, so React unmounts and remounts its
// whole subtree on each keystroke — which drops focus and sends the caret out of
// the input you are typing in (and resets tab position). Keep section/field
// helpers out here and pass what they need as props.
function Section({ label, hint, children, T }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.25 }}>
        <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 1, color: T.TEXT_TER }}>{label}</Typography>
        {hint && <Typography sx={{ fontSize: '0.66rem', color: T.TEXT_TER }}>{hint}</Typography>}
        <Box sx={{ flex: 1, height: '1px', bgcolor: T.BD }} />
      </Box>
      {children}
    </Box>
  );
}

// ── Pallet rows ─────────────────────────────────────────────────────────────
// Two memoized components, so that typing in ONE pallet does not draw the other thirty-two again. The form keeps every pallet
// in one array and used to re-render the whole form on each keystroke - unnoticeable on a desktop, seconds per key on a phone
// with a 33-pallet list (measured on the production build under a 6x CPU slow-down: 3.6 s a keystroke, 4 s a move). A `pallet`
// object keeps its identity until IT changes (the mutators copy only the row they edit) and every handler is stable and takes
// the row's `_key`, so React.memo's shallow compare skips every row that did not change. The head (No, arrows, ID, reference,
// processing) and the item area (sizes, counts, m², the specification finder) are separate memos: editing a reference does not
// redraw the item rows. Keep the handlers stable (useCallback, refs and updater functions only) or the memo stops paying.

// Phone sizing, for this form's own drawer only. iOS Safari zooms the whole page when a field with text under 16px takes
// focus, and the compact type that suits a pointer is wrong for a thumb; 44px is the smallest comfortable touch target (the job
// report form follows the same rule). `!important` because several fields set their own size (monospace identifiers, the item
// code) and the zoom happens whatever the reason for the small text.
const PHONE_TARGET = 44;
const PHONE_BTN = { width: PHONE_TARGET, height: PHONE_TARGET };
const PHONE_FORM_SX = {
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { minHeight: PHONE_TARGET },
  '& input:not([type=checkbox]):not([type=radio]), & textarea, & select': { fontSize: '16px !important' },
  '& .MuiToggleButton-root': { minHeight: PHONE_TARGET },
};

const FIELD = { size: 'small', InputLabelProps: { shrink: true } };
// A figure in the item grid: right-aligned tabular numbers without the spinner arrows stealing width. Geometry comes from the grid.
const NUM_CELL_SX = {
  ...NO_SPINNERS,
  minWidth: 0,
  '& input': { textAlign: 'right', fontVariantNumeric: 'tabular-nums', px: 1 },
};
// the value selects on focus, so a wrong entry can just be overtyped
const SELECT_ON_FOCUS = { onFocus: (e) => e.target.select() };

// The sizes, counts and m² of one pallet, with its "add row" / "find by specification" row.
const PalletItems = memo(function PalletItems({
  palletKey, items, isXs, T, type, scopePrefixes, codeOptions, specOpen, canCreateVariety, searchProducts,
  onAddItem, onRemoveItem, onItemField, onResetSqm, onToggleSpec, onUseBuilt,
}) {
  const { t } = useTranslation();
  const pt = items.reduce((acc, it) => {
    acc.pcs += Number(it.pcs) || 0;
    acc.sqm += Number(it.sqm) || 0;
    return acc;
  }, { pcs: 0, sqm: 0 });
  // On a phone the column headers are gone, so every field carries its own label; on a desktop the header row says it once
  // and a label here would be noise.
  const labelled = (text) => (isXs ? { label: text, InputLabelProps: { shrink: true } } : {});
  const footerButton = { fontSize: '0.66rem', textTransform: 'none', ...(isXs ? { minHeight: PHONE_TARGET, fontSize: '0.8rem', px: 1.5 } : {}) };

  return (
    <>
      {/* Column headers belong to the desktop table only - on a phone each row is a card and carries its own labels. */}
      {!isXs && (
        <Box sx={{ display: 'grid', gridTemplateColumns: ITEM_GRID, gap: 0.75,
          px: 1.5, pt: 1.25, pb: 0.5, alignItems: 'center' }}>
          {[
            { label: t('mis.plColCode'), align: 'start' },
            { label: t('mis.plColLength') },
            { label: t('mis.plColWidth') },
            { label: t('mis.plColThickness') },
            { label: t('mis.plColPcs') },
            { label: t('mis.plColSqm') },
          ].map((c) => (
            <Typography key={c.label} noWrap sx={{ minWidth: 0,
              fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.4,
              textTransform: 'uppercase', color: T.TEXT_TER,
              textAlign: c.align === 'start' ? 'start' : 'end' }}>
              {c.label}
            </Typography>
          ))}
          <Box />
        </Box>
      )}

      {/* Item rows */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        {items.map((it, iIdx) => {
          const derived = deriveSqm(it);
          const isAuto = !it.sqmTouched && derived !== null;
          const badCode = type === 'linked' && it.code && scopePrefixes.size > 0
            && !scopePrefixes.has(productPrefix(it.code));
          return (
            <Box key={iIdx} sx={isXs
              ? { mb: 1.25, p: 1.25, borderRadius: '12px',
                  border: `1px solid ${T.BD}`, display: 'grid',
                  gridTemplateColumns: '1fr 1fr', gap: 1.25, alignItems: 'end' }
              : { display: 'grid', gridTemplateColumns: ITEM_GRID, gap: 0.75,
                  mb: 0.75, alignItems: 'center' }}>
              <Box sx={{ minWidth: 0, gridColumn: isXs ? '1 / -1' : 'auto' }}>
                {type === 'linked' ? (
                  <Autocomplete freeSolo options={codeOptions} fullWidth
                    value={it.code || ''}
                    onInputChange={(_, v) => onItemField(palletKey, iIdx, 'code', v)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" error={Boolean(badCode)}
                        {...labelled(t('mis.plColCode'))}
                        placeholder={t('mis.itemCodePlaceholder')}
                        sx={{ '& input': { fontFamily: 'monospace', fontSize: isXs ? '0.95rem' : '0.75rem' } }} />
                    )} />
                ) : (
                  <TextField size="small" value={it.code} fullWidth
                    {...labelled(t('mis.plColCode'))}
                    placeholder={t('mis.itemCodePlaceholder')}
                    onChange={(e) => onItemField(palletKey, iIdx, 'code', e.target.value)}
                    sx={{ '& input': { fontFamily: 'monospace', fontSize: isXs ? '0.95rem' : '0.75rem' } }} />
                )}
              </Box>
              <TextField size="small" type="number" value={it.lengthCm} sx={NUM_CELL_SX}
                {...labelled(t('mis.plColLength'))}
                {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'decimal', min: 0 }}
                onChange={(e) => onItemField(palletKey, iIdx, 'lengthCm', e.target.value)} />
              <TextField size="small" type="number" value={it.widthCm} sx={NUM_CELL_SX}
                {...labelled(t('mis.plColWidth'))}
                {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'decimal', min: 0 }}
                onChange={(e) => onItemField(palletKey, iIdx, 'widthCm', e.target.value)} />
              <TextField size="small" type="number" value={it.thicknessCm} sx={NUM_CELL_SX}
                {...labelled(t('mis.plColThickness'))}
                {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'decimal', step: '0.1', min: 0 }}
                onChange={(e) => onItemField(palletKey, iIdx, 'thicknessCm', e.target.value)} />
              <TextField size="small" type="number" value={it.pcs} sx={NUM_CELL_SX}
                {...labelled(t('mis.plColPcs'))}
                {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'numeric', step: '1', min: 0 }}
                onChange={(e) => onItemField(palletKey, iIdx, 'pcs', e.target.value)} />
              <Tooltip title={isAuto ? t('mis.plSqmAuto') : t('mis.plSqmManual')}>
                <TextField size="small" type="number" value={it.sqm} sx={{
                  ...NUM_CELL_SX,
                  '& .MuiOutlinedInput-root': isAuto ? { bgcolor: T.CTRL_BG } : {},
                }}
                  {...labelled(t('mis.plColSqm'))}
                  {...SELECT_ON_FOCUS} inputProps={{ inputMode: 'decimal', min: 0 }}
                  onChange={(e) => onItemField(palletKey, iIdx, 'sqm', e.target.value)}
                  InputProps={{ endAdornment: isAuto
                    ? <AutoAwesomeIcon sx={{ fontSize: 11, color: T.TEXT_TER, ml: 0.25 }} />
                    : (derived !== null && (
                        <Tooltip title={t('mis.plSqmRecalc')}>
                          {/* a finger needs more than the 16px a pointer gets */}
                          <IconButton size="small" aria-label={t('mis.plSqmRecalc')}
                            sx={isXs ? { width: 40, height: 40, p: 0, mr: -1 } : { p: 0.15 }}
                            onClick={() => onResetSqm(palletKey, iIdx)}>
                            <AutoAwesomeIcon sx={{ fontSize: isXs ? 18 : 12 }} />
                          </IconButton>
                        </Tooltip>
                      )) }} />
              </Tooltip>
              <Box sx={{ display: 'flex', justifyContent: isXs ? 'flex-end' : 'center',
                minWidth: 0, gridColumn: isXs ? '1 / -1' : 'auto' }}>
                {items.length > 1 && (
                  <IconButton size={isXs ? 'medium' : 'small'} onClick={() => onRemoveItem(palletKey, iIdx)}
                    aria-label={t('common.delete')} sx={isXs ? PHONE_BTN : undefined}>
                    <DeleteOutlineIcon sx={{ fontSize: isXs ? 18 : 14, color: T.TEXT_TER }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          );
        })}

        {/* Pallet footer - add row + subtotal */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: isXs ? 0.5 : 0, mt: 0.5 }}>
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
            onClick={() => onAddItem(palletKey)}
            sx={{ ...footerButton, color: T.TEXT_SEC }}>
            {t('mis.addItemRowButton')}
          </Button>
          <Button size="small" startIcon={<StraightenIcon sx={{ fontSize: 13 }} />}
            onClick={() => onToggleSpec(palletKey)}
            aria-expanded={specOpen}
            sx={{ ...footerButton, color: specOpen ? T.TEXT_PRI : T.TEXT_SEC }}>
            {specOpen ? t('inventory.specHide') : t('inventory.specToggle')}
          </Button>
          <Box sx={{ marginInlineStart: 'auto', display: 'flex', gap: 1.5, paddingInlineEnd: isXs ? 0 : 3.5 }}>
            <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC }}>
              {t('mis.plColPcs')} <b style={{ color: T.TEXT_PRI }}>{pt.pcs}</b>
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC }}>
              {t('mis.plColSqm')} <b style={{ color: T.TEXT_PRI }}>{pt.sqm.toFixed(2)}</b>
            </Typography>
          </Box>
        </Box>
        {specOpen && (
          <Box sx={{ mt: 1 }}>
            <SpecCodeBuilder searchProducts={searchProducts} showInventory={false}
              canCreate={canCreateVariety} noPermissionHint={t('inventory.specCannotAdd')}
              usedCodes={new Set(items.map((it) => String(it.code || '').toUpperCase()))}
              onUse={(product, variant) => onUseBuilt(palletKey, product, variant)} />
          </Box>
        )}
      </Box>
    </>
  );
});

// One pallet of the list: its head (place, arrows, ID, reference, processing, copy, remove) and its item area.
const PalletCard = memo(function PalletCard({
  pallet, index, isLast, canRemove, isMoved, clashIdText, clashRefText, isXs, T, type, scopePrefixes, codeOptions,
  specOpen, canCreateVariety, searchProducts,
  onMoveBy, onMoveTo, onDuplicate, onRemove, onField,
  onAddItem, onRemoveItem, onItemField, onResetSqm, onToggleSpec, onUseBuilt,
}) {
  const { t } = useTranslation();
  const key = pallet._key;
  const arrowSx = isXs ? PHONE_BTN : { p: 0.1 };
  const iconBtnSx = isXs ? PHONE_BTN : undefined;

  // On a phone the head is three lines - [No, arrows, copy, remove] / [ID, reference] / [processing] - because one line of
  // seven controls leaves each too small to hit. The copy / remove buttons are placed before the fields on a phone and
  // after them on a desktop, so the tab order follows what is drawn either way.
  const rowButtons = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(isXs ? { marginInlineStart: 'auto', gap: 0.5 } : {}) }}>
      <Tooltip title={t('mis.plDuplicatePallet')}>
        <IconButton size="small" onClick={() => onDuplicate(key)} aria-label={t('mis.plDuplicatePallet')} sx={iconBtnSx}>
          <ContentCopyIcon sx={{ fontSize: isXs ? 18 : 15, color: T.TEXT_TER }} />
        </IconButton>
      </Tooltip>
      {canRemove && (
        <Tooltip title={t('mis.plRemovePallet')}>
          <IconButton size="small" onClick={() => onRemove(key)} aria-label={t('mis.plRemovePallet')} sx={iconBtnSx}>
            <DeleteOutlineIcon sx={{ fontSize: isXs ? 20 : 16, color: T.TEXT_TER }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box data-pallet-key={key}
      sx={{ border: `1px solid ${isMoved ? T.TEXT_SEC : T.BD}`, borderRadius: '12px', overflow: 'hidden',
        transition: 'border-color 0.4s',
        // the row that was just moved / added pulses once, so it can be found again
        '@keyframes plMovedPulse': {
          '0%': { boxShadow: `0 0 0 0 ${T.PULSE}` },
          '30%': { boxShadow: `0 0 0 5px ${T.PULSE}` },
          '100%': { boxShadow: '0 0 0 0 transparent' },
        },
        animation: isMoved ? 'plMovedPulse 1.2s ease-out' : 'none',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' } }}>

      {/* Pallet head */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, flexWrap: 'wrap',
        bgcolor: T.CTRL_BG, borderBottom: `1px solid ${T.BD}` }}>
        {/* Its place in the list - the "No" the printed list shows. Type another number to move it there. */}
        <PalletNoInput place={index + 1} palletKey={key} index={index}
          label={t('mis.plNoField')} help={t('mis.plNoHelp')} onMove={onMoveTo} />
        <Box sx={{ display: 'flex', flexDirection: isXs ? 'row' : 'column', mx: isXs ? 0 : -0.25 }}>
          <Tooltip title={t('mis.plMoveUp')}>
            <span>
              <IconButton size="small" disabled={index === 0} onClick={() => onMoveBy(key, -1)}
                aria-label={t('mis.plMoveUp')} data-testid={`pl-pallet-up-${index}`} sx={arrowSx}>
                <KeyboardArrowUpIcon sx={{ fontSize: isXs ? 24 : 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('mis.plMoveDown')}>
            <span>
              <IconButton size="small" disabled={isLast} onClick={() => onMoveBy(key, 1)}
                aria-label={t('mis.plMoveDown')} data-testid={`pl-pallet-down-${index}`} sx={arrowSx}>
                <KeyboardArrowDownIcon sx={{ fontSize: isXs ? 24 : 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {isXs && rowButtons}
        {/* a line break: the fields start a new line on a phone, whatever the width */}
        {isXs && <Box sx={{ flexBasis: '100%', height: 0 }} />}
        <TextField {...FIELD} label={t('mis.palletIdField')} value={pallet.palletId}
          onChange={(e) => onField(key, 'palletId', e.target.value)}
          error={Boolean(clashIdText)} data-pallet-clash={clashIdText ? 'true' : undefined}
          sx={isXs ? { flex: '1 1 0', minWidth: 0 } : { width: 96 }} />
        <TextField {...FIELD} label={t('mis.referenceField')} value={pallet.reference}
          onChange={(e) => onField(key, 'reference', e.target.value)}
          error={Boolean(clashRefText)} data-pallet-clash={clashRefText ? 'true' : undefined}
          sx={isXs ? { flex: '1 1 0', minWidth: 0 } : { width: 110 }} />
        <Autocomplete freeSolo options={PROCESSING_SUGGESTIONS}
          value={pallet.processingType || ''}
          onInputChange={(_, v) => onField(key, 'processingType', v)}
          sx={isXs ? { flex: '1 1 100%', minWidth: 0 } : { flex: 1, minWidth: 130 }}
          renderInput={(params) => (
            <TextField {...params} {...FIELD} label={t('mis.processingTypeField')} />
          )} />
        {!isXs && rowButtons}
      </Box>

      {(clashIdText || clashRefText) && (
        <Box role="alert" sx={{ px: 1.5, py: 0.5, borderBottom: `1px solid ${T.BD}` }}>
          {clashIdText && (
            <Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>
              {t('mis.plDuplicateIdField', { pallets: clashIdText })}
            </Typography>
          )}
          {clashRefText && (
            <Typography sx={{ fontSize: '0.68rem', color: 'error.main' }}>
              {t('mis.plDuplicateRefField', { pallets: clashRefText })}
            </Typography>
          )}
        </Box>
      )}

      <PalletItems palletKey={key} items={pallet.items} isXs={isXs} T={T} type={type}
        scopePrefixes={scopePrefixes} codeOptions={codeOptions} specOpen={specOpen}
        canCreateVariety={canCreateVariety} searchProducts={searchProducts}
        onAddItem={onAddItem} onRemoveItem={onRemoveItem} onItemField={onItemField}
        onResetSqm={onResetSqm} onToggleSpec={onToggleSpec} onUseBuilt={onUseBuilt} />
    </Box>
  );
});

// preset (new lists only, optional) — raised from a Supply record:
//   supplyRecordId — the list is linked to that record (shows under it)
//   recordTitle    — shown in the banner, so it's clear where it will land
//   productCode    — seeds the first pallet with the record's product
// draft (new lists only, optional) — a stored unsent list (components/mis/misDraftStrip.js) to
// carry on with. A new list is kept in this browser while it is being filled
// (tools/hooks/useFormDraft.js); nothing reaches the server until Save.
export default function PackingListForm({ open, onClose, packingList = null, preset = null, draft = null }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const { activeBranchId, activeBranch } = useBranch();
  const { can } = usePermissions();
  const canCreateVariety = can('inventory:subproduct:create');

  const isEdit = Boolean(packingList);
  // The supply record a list was raised from survives in its draft too.
  const effPreset = (!isEdit && draft && draft.snapshot && draft.snapshot.preset) || preset;

  const T = useMemo(() => ({
    BD:       isDark ? 'rgba(255,255,255,0.07)' : theme.palette.divider,
    BD2:      isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    TEXT_PRI: isDark ? 'rgba(255,255,255,0.87)' : theme.palette.text.primary,
    TEXT_SEC: isDark ? 'rgba(255,255,255,0.45)' : theme.palette.text.secondary,
    TEXT_TER: isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.3)',
    CTRL_BG:  isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    PULSE:    isDark ? 'rgba(255,255,255,0.2)'  : 'rgba(0,0,0,0.14)',
  }), [isDark, theme.palette.divider, theme.palette.text.primary, theme.palette.text.secondary]);

  const [type, setType] = useState('free');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);   // full docs (need lineItems for scope)
  const [driverInfo, setDriverInfo] = useState({ fullName: '', nationalId: '', smartNumber: '', phone: '', iban: '' });
  const [vehicleInfo, setVehicleInfo] = useState({ trailerPlateNumber: '', trailerSmartNumber: '' });
  const [customsAgent, setCustomsAgent] = useState({ name: '', phone: '' });
  const [loadingOfficer, setLoadingOfficer] = useState({ name: '', phone: '' });
  const [originAddress, setOriginAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [shippingDestination, setShippingDestination] = useState('');
  const [standardThicknessCm, setStandardThicknessCm] = useState('');
  const [stoneNames, setStoneNames] = useState([]);     // [{ name } | { productId, code, name, nameAr, nameFa }]
  const [pallets, setPallets] = useState(() => [withKey({ ...EMPTY_PALLET })]);
  const [specKey, setSpecKey] = useState(null);   // _key of the pallet whose specification finder is open
  // Re-ordering: the row that just moved (briefly highlighted, and scrolled into view, so it can be found again).
  const [movedKey, setMovedKey] = useState(null);
  const [announce, setAnnounce] = useState('');          // the move said aloud, for a screen reader
  const exitingRef = useRef(new Set());                  // rows that are folding away (a second click must not remove another)
  // Motion (tools/hooks/useFlipList.js): a pallet that is moved, added, duplicated or removed does not jump - the rows slide
  // to their new places, a new row eases in, a removed one folds away. Only a request to move is animated (capture() runs
  // just before such a change), never typing; and `prefers-reduced-motion` turns all of it off.
  const orderSignature = pallets.map((p) => p._key).join('|');
  const flip = useFlipList(orderSignature, { selector: '[data-pallet-key]', attr: 'palletKey', liftKey: movedKey });
  // A pallet whose place changed - because it was moved, or because a row above it came or went - rolls its number: the
  // new figure slides in from the way the pallet travelled (up when the number got smaller), so the eye follows the change.
  const placesRef = useRef(new Map());
  useLayoutEffect(() => {
    const root = flip.containerRef.current;
    const before = placesRef.current;
    const now = new Map(pallets.map((p, i) => [p._key, i + 1]));
    placesRef.current = now;
    if (!root || !before.size || prefersReducedMotion()) return;
    now.forEach((place, key) => {
      const was = before.get(key);
      if (was === undefined || was === place) return;
      const box = root.querySelector(`input[data-pallet-no="${key}"]`);
      if (!box || typeof box.animate !== 'function') return;
      box.getAnimations().forEach((a) => { if (a.id === 'flip-no') a.cancel(); });   // a quick second move starts from rest
      const roll = box.animate(
        [{ transform: `translateY(${place < was ? 9 : -9}px)`, opacity: 0.1 }, { transform: 'none', opacity: 1 }],
        { duration: 320, easing: 'cubic-bezier(0.22, 0.9, 0.3, 1)' },
      );
      try { roll.id = 'flip-no'; } catch (_) { /* a read-only id changes nothing */ }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSignature]);
  const [status, setStatus] = useState('draft');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The day the list is dated, and the calendar it is entered and printed in.
  const [issueDate, setIssueDate] = useState(() => toIsoDay(today()));
  const [dateSystem, setDateSystem] = useState('gregorian');
  // The unsent draft of a NEW list: every opening has its own key, a continued one re-uses its key.
  const [draftKey, setDraftKey] = useState('');
  const [fromDraft, setFromDraft] = useState(false);
  const [restoringIds, setRestoringIds] = useState([]);   // linked invoices of a restored draft, still being fetched
  const [hydratedAt, setHydratedAt] = useState(0);
  const [baseline, setBaseline] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  // The packing list NUMBER: automatic (the branch's next one, which the server suggests) until the person
  // types their own; on an existing list it is the current number and can be changed.
  const [docNumber, setDocNumber] = useState('');
  const [numberTouched, setNumberTouched] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [numberError, setNumberError] = useState('');
  const numberRef = useRef(null);

  // ── hydrate ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setError(''); setSpecKey(null); setConfirmClose(false);
    setFromDraft(false); setRestoringIds([]); setNumberError('');
    if (packingList) {
      setDocNumber(String(packingList.docNumber)); setNumberTouched(false);
      // a list made before lists were dated has no date of its own: the day it was created
      setIssueDate(toIsoDay(packingList.issueDate || packingList.insertDate) || toIsoDay(today()));
      setDateSystem(normalizeDateSystem(packingList.dateSystem));
      setType(packingList.type);
      setDriverInfo({ fullName: '', nationalId: '', smartNumber: '', phone: '', iban: '', ...packingList.driverInfo });
      setVehicleInfo({ trailerPlateNumber: '', trailerSmartNumber: '', ...packingList.vehicleInfo });
      setCustomsAgent({ name: '', phone: '', ...packingList.customsAgent });
      setLoadingOfficer({ name: '', phone: '', ...packingList.loadingOfficer });
      setOriginAddress(packingList.originAddress || '');
      setDestinationAddress(packingList.destinationAddress || '');
      setShippingDestination(packingList.shippingDestination || '');
      setStandardThicknessCm(packingList.standardThicknessCm ?? '');
      setStoneNames(Array.isArray(packingList.stoneNames) ? packingList.stoneNames.map((s) => ({ ...s })) : []);
      setPallets(packingList.pallets?.length
        ? packingList.pallets.map((p) => withKey({
            ...p,
            items: p.items?.length
              ? p.items.map((it) => ({ ...it, sqmTouched: true }))   // stored values win until edited
              : [{ ...EMPTY_ITEM }],
          }))
        : [withKey({ ...EMPTY_PALLET })]);
      setStatus(packingList.status || 'draft');
      setNotes(packingList.notes || '');
      setSelectedInvoices([]);   // resolved by the effect below (needs real docs, not bare ids)
    } else if (draft && draft.snapshot) {
      // Carrying on an unsent list: the form opens exactly as it was left.
      const d = draft.snapshot;
      setDraftKey(draft.key);
      setFromDraft(true);
      // a number the person typed is kept; an untouched one is the suggestion, fetched afresh
      setDocNumber(d.docNumberTouched && d.docNumber ? String(d.docNumber) : '');
      setNumberTouched(Boolean(d.docNumberTouched && d.docNumber));
      setIssueDate(d.issueDate || toIsoDay(today()));
      setDateSystem(normalizeDateSystem(d.dateSystem));
      setType(d.type === 'linked' ? 'linked' : 'free');
      setSelectedInvoices([]);
      setRestoringIds(Array.isArray(d.invoiceIds) ? d.invoiceIds : []);
      setDriverInfo({ fullName: '', nationalId: '', smartNumber: '', phone: '', iban: '', ...d.driverInfo });
      setVehicleInfo({ trailerPlateNumber: '', trailerSmartNumber: '', ...d.vehicleInfo });
      setCustomsAgent({ name: '', phone: '', ...d.customsAgent });
      setLoadingOfficer({ name: '', phone: '', ...d.loadingOfficer });
      setOriginAddress(d.originAddress || ''); setDestinationAddress(d.destinationAddress || '');
      setShippingDestination(d.shippingDestination || '');
      setStandardThicknessCm(d.standardThicknessCm == null ? '' : d.standardThicknessCm);
      setStoneNames(Array.isArray(d.stoneNames) ? d.stoneNames : []);
      setPallets(Array.isArray(d.pallets) && d.pallets.length
        ? d.pallets.map((p) => withKey({ ...p, items: p.items && p.items.length ? p.items.map((it) => ({ ...it })) : [{ ...EMPTY_ITEM }] }))
        : [withKey({ ...EMPTY_PALLET })]);
      setStatus(d.status === 'final' ? 'final' : 'draft'); setNotes(d.notes || '');
    } else {
      setDraftKey(newDraftKey('packingList', activeBranchId));
      setDocNumber(''); setNumberTouched(false);
      setIssueDate(toIsoDay(today()));
      setDateSystem(branchDocDefaults(activeBranch).dateSystem);
      setType('free'); setSelectedInvoices([]);
      setDriverInfo({ fullName: '', nationalId: '', smartNumber: '', phone: '', iban: '' });
      setVehicleInfo({ trailerPlateNumber: '', trailerSmartNumber: '' });
      setCustomsAgent({ name: '', phone: '' });
      setLoadingOfficer({ name: '', phone: '' });
      setOriginAddress(''); setDestinationAddress(''); setShippingDestination('');
      setStandardThicknessCm('');
      setStoneNames([]);
      setPallets(preset?.productCode
        ? [withKey({ ...EMPTY_PALLET, palletId: 'P1', productCode: preset.productCode,
            items: [{ ...EMPTY_ITEM, code: preset.productCode }] })]
        : [withKey({ ...EMPTY_PALLET })]);
      setStatus('draft'); setNotes('');
    }
    // What "nothing has been changed yet" looks like is read from the render that follows.
    setBaseline(null);
    setHydratedAt(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packingList, draft]);

  // Edit mode (and a restored draft): turn stored invoiceIds into REAL documents, so the chips show
  // "#12 — Customer" instead of a raw ObjectId (and so scope suggestions work).
  useEffect(() => {
    const ids = isEdit ? (packingList?.invoiceIds || []) : ((draft && draft.snapshot && draft.snapshot.invoiceIds) || []);
    if (!open || ids.length === 0) return;
    let cancelled = false;
    Promise.all(ids.map((id) =>
      authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/invoices/${id}` })
        .then((r) => r.data.data || r.data)
        .catch(() => null)
    )).then((docs) => {
      if (!cancelled) { setSelectedInvoices(docs.filter(Boolean)); setRestoringIds([]); }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, packingList?._id, draft]);

  // Invoice search (linked mode only)
  useEffect(() => {
    if (!open || type !== 'linked' || !activeBranchId) { setInvoiceOptions([]); return; }
    const timer = setTimeout(() => {
      authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/invoices`,
        params: { branchId: activeBranchId, docType: 'invoice', limit: 10,
          ...(invoiceSearch.trim() ? { search: invoiceSearch.trim() } : {}) },
      })
        .then((res) => setInvoiceOptions(res.data.data || []))
        .catch(() => setInvoiceOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [open, type, invoiceSearch, activeBranchId, authCtx, axiosGlobal]);

  // ── scope: what codes the linked invoices actually allow ──────────────────
  const scope = useMemo(() => {
    const codes = new Set(), prefixes = new Set();
    for (const inv of selectedInvoices) {
      for (const li of (inv.lineItems || [])) {
        if (!li.code) continue;
        codes.add(li.code);
        prefixes.add(productPrefix(li.code));
      }
    }
    return { codes: [...codes], prefixes };
  }, [selectedInvoices]);

  // Suggestions offered on the item code field: the exact SKUs from the linked
  // invoices first, then their bare product codes (a packing item is often the
  // nominal code, not the full SKU).
  const codeOptions = useMemo(() => {
    if (type !== 'linked') return [];
    return [...scope.codes, ...[...scope.prefixes].filter((p) => !scope.codes.includes(p))];
  }, [type, scope]);

  const outOfScope = useMemo(() => {
    if (type !== 'linked' || scope.prefixes.size === 0) return [];
    const bad = [];
    for (const p of pallets) {
      for (const it of (p.items || [])) {
        if (it.code && it.code.trim() && !scope.prefixes.has(productPrefix(it.code))) bad.push(it.code);
      }
    }
    return [...new Set(bad)];
  }, [type, pallets, scope]);

  // ── totals ────────────────────────────────────────────────────────────────
  const palletTotals = (p) => (p.items || []).reduce((acc, it) => {
    acc.pcs += Number(it.pcs) || 0;
    acc.sqm += Number(it.sqm) || 0;
    return acc;
  }, { pcs: 0, sqm: 0 });

  const totals = pallets.reduce((acc, p) => {
    const pt = palletTotals(p);
    acc.pcs += pt.pcs; acc.sqm += pt.sqm;
    return acc;
  }, { pcs: 0, sqm: 0 });

  const clashes = useMemo(() => palletClashes(pallets), [pallets]);

  // What the person is told, in words, about the pallets that clash.
  const clashMessage = (groups) => groups.map((g) => (g.field === 'reference'
    ? t('mis.plDuplicateRefError', { pallets: g.pallets.join(', '), value: g.value })
    : t('mis.plDuplicateIdError', { value: g.value }))).join(' ');

  // ── mutators ──────────────────────────────────────────────────────────────
  // Every pallet handler takes the pallet's `_key` (never its position) and is STABLE: it reaches the latest values through
  // refs and updater functions only. That is what lets the memoized rows (PalletCard / PalletItems, above) skip a render when
  // some OTHER pallet changed - the cost of a keystroke on a phone used to be "draw every pallet again".
  const palletsRef = useRef(pallets);
  palletsRef.current = pallets;
  const thicknessRef = useRef(standardThicknessCm);
  thicknessRef.current = standardThicknessCm;
  const { capture: flipCapture, exit: flipExit } = flip;

  // A new row eases in where it lands and is brought into view (it is "the moved one" for the highlight and the scroll).
  const addPallet = () => {
    const key = newPalletKey();
    flipCapture();
    setPallets((ps) => [...ps, {
      ...EMPTY_PALLET,
      _key: key,
      palletId: freePalletId(ps),
      items: [{ ...EMPTY_ITEM, thicknessCm: standardThicknessCm }],
    }]);
    setMovedKey(key);
  };

  const duplicatePallet = useCallback((key) => {
    const copyKey = newPalletKey();
    flipCapture();
    setSpecKey(null);
    setPallets((ps) => {
      const idx = ps.findIndex((p) => p._key === key);
      if (idx < 0) return ps;
      const src = ps[idx];
      const copy = {
        ...src,
        _key: copyKey,
        palletId: copyPalletId(ps, src.palletId),
        reference: '',
        items: src.items.map((it) => ({ ...it })),
      };
      return [...ps.slice(0, idx + 1), copy, ...ps.slice(idx + 1)];
    });
    setMovedKey(copyKey);
  }, [flipCapture]);

  // The row folds away first, and only then leaves the list - so the rows below close up with it instead of jumping. The
  // last pallet stays (counting the rows that are already folding away, so two quick taps cannot empty the list).
  const removePallet = useCallback((key) => {
    if (!key || exitingRef.current.has(key)) return;
    if (palletsRef.current.length - exitingRef.current.size <= 1) return;
    exitingRef.current.add(key);
    setSpecKey(null);
    flipExit(key, () => {
      setPallets((ps) => ps.filter((p) => p._key !== key));
      exitingRef.current.delete(key);
    });
  }, [flipExit]);

  // Re-ordering. The pallets print in the order they are listed here, and the "No" is simply the place in that order.
  // A move that changes nothing (the same place, or the end of the list) does nothing at all - it must not close the
  // specification finder or light up the row. The row is found by its key INSIDE the updater as well, so the move is
  // right even if the list changed since this render.
  const reorder = useCallback((key, targetOf) => {
    const list = palletsRef.current;
    const here = list.findIndex((p) => p._key === key);
    const there = here < 0 ? -1 : Math.max(0, Math.min(list.length - 1, targetOf(here)));
    if (here < 0 || there === here) return;
    flipCapture();   // where every row is NOW, so each one can slide from there to where it is next
    setPallets((ps) => {
      const from = ps.findIndex((p) => p._key === key);
      if (from < 0) return ps;
      const target = Math.max(0, Math.min(ps.length - 1, targetOf(from)));
      if (target === from) return ps;
      const next = ps.slice();
      const [row] = next.splice(from, 1);
      next.splice(target, 0, row);
      return next;
    });
    setSpecKey(null);   // a move closes the specification finder: it would be in the way of the row sliding past
    setMovedKey(key);
    setAnnounce(t('mis.plMovedTo', { pallet: String(list[here].palletId || '').trim() || here + 1, place: there + 1 }));
  }, [flipCapture, t]);
  const movePalletBy = useCallback((key, delta) => reorder(key, (from) => from + delta), [reorder]);
  // The place typed in a row's "No" box (1-based; past the end means last).
  const movePalletTo = useCallback((key, place) => reorder(key, () => place - 1), [reorder]);
  useEffect(() => {
    if (!movedKey) return undefined;
    const row = document.querySelector(`[data-pallet-key="${movedKey}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    const timer = setTimeout(() => setMovedKey(null), 1600);
    return () => clearTimeout(timer);
  }, [movedKey]);

  const setPalletField = useCallback((key, field, value) =>
    setPallets((ps) => ps.map((p) => (p._key === key ? { ...p, [field]: value } : p))), []);

  const addItem = useCallback((key) => setPallets((ps) => ps.map((p) => (
    p._key === key
      ? { ...p, items: [...p.items, { ...EMPTY_ITEM, thicknessCm: thicknessRef.current, code: p.productCode || '' }] }
      : p
  ))), []);

  // Products + their varieties for the specification builder: this branch's own catalogue.
  const searchForBuilder = useCallback(async (term) => {
    const res = await authCtx.jwtInst({
      method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/products-lookup`,
      params: { search: String(term || '').trim(), branchId: activeBranchId },
    });
    return res.data || [];
  }, [authCtx, axiosGlobal, activeBranchId]);

  // A variety from the specification builder becomes an item of that pallet: it fills the
  // pallet's empty row (or adds one) with the variety's code and its own size. An unsized slab
  // brings only its thickness — the real cut is typed, as for any other row.
  const addBuiltItem = useCallback((key, product, variant) => {
    const parsed = parseStoneCode(variant.code);
    const sized = parsed && parsed.valid && !parsed.unsized;
    setPallets((ps) => ps.map((p) => {
      if (p._key !== key) return p;
      const blankAt = p.items.findIndex((it) => isBlankRow(it, p));
      const base = blankAt >= 0 ? p.items[blankAt] : { ...EMPTY_ITEM, thicknessCm: thicknessRef.current };
      const item = {
        ...base,
        code: variant.code,
        ...(sized ? { lengthCm: String(parsed.lengthCm), widthCm: String(parsed.widthCm) } : {}),
        ...(parsed && parsed.valid ? { thicknessCm: String(parsed.thicknessMm / 10) } : {}),
        sqmTouched: false,
      };
      const items = blankAt >= 0 ? p.items.map((it, j) => (j === blankAt ? item : it)) : [...p.items, item];
      return { ...p, productCode: p.productCode || product.code, items };
    }));
  }, []);

  const removeItem = useCallback((key, iIdx) => setPallets((ps) => ps.map((p) => (
    p._key === key ? { ...p, items: p.items.filter((_, j) => j !== iIdx) } : p
  ))), []);

  // Editing L / W / Pcs re-derives m² unless the user has typed their own.
  const setItemField = useCallback((key, iIdx, field, value) => setPallets((ps) => ps.map((p) => {
    if (p._key !== key) return p;
    return {
      ...p,
      items: p.items.map((it, j) => {
        if (j !== iIdx) return it;
        const next = { ...it, [field]: value };
        if (field === 'sqm') { next.sqmTouched = value !== ''; return next; }
        if (!next.sqmTouched && ['lengthCm', 'widthCm', 'pcs'].includes(field)) {
          const derived = deriveSqm(next);
          if (derived !== null) next.sqm = derived;
        }
        return next;
      }),
    };
  })), []);

  const resetSqm = useCallback((key, iIdx) => setPallets((ps) => ps.map((p) => {
    if (p._key !== key) return p;
    return { ...p, items: p.items.map((it, j) => {
      if (j !== iIdx) return it;
      const derived = deriveSqm(it);
      return { ...it, sqmTouched: false, sqm: derived === null ? '' : derived };
    }) };
  })), []);

  const toggleSpec = useCallback((key) => setSpecKey((cur) => (cur === key ? null : key)), []);

  // Applying a standard thickness fills every blank thickness cell.
  const applyStandardThickness = (value) => {
    setStandardThicknessCm(value);
    if (value === '') return;
    setPallets((ps) => ps.map((p) => ({
      ...p,
      items: p.items.map((it) => (it.thicknessCm === '' || it.thicknessCm === undefined || it.thicknessCm === null
        ? { ...it, thicknessCm: value } : it)),
    })));
  };

  // ── the number ───────────────────────────────────────────────────────────────
  // What a new list would get right now. It is only LOOKED at: if the person leaves it alone, the server takes
  // the next free number when the list is saved (so two people filling a form at once never clash).
  useEffect(() => {
    if (!open || isEdit || !activeBranchId) return undefined;
    let cancelled = false;
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/packing-lists/next-number`, params: { branchId: activeBranchId } })
      .then((res) => { if (!cancelled) setSuggested(Number(res.data && res.data.next) || null); })
      .catch(() => { if (!cancelled) setSuggested(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, activeBranchId, hydratedAt]);

  // A number the person typed that another list of the branch already carries is flagged as they type, not
  // after the whole list is filled in. The save checks again: this answer can be a moment out of date.
  useEffect(() => {
    if (!open || !(isEdit || numberTouched)) return undefined;   // an automatic number is free by construction
    const digits = String(docNumber || '');
    if (!digits || (isEdit && packingList && Number(digits) === Number(packingList.docNumber))) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      authCtx.jwtInst({
        method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/packing-lists/number-available`,
        params: { branchId: isEdit && packingList ? packingList.branchId : activeBranchId, number: digits, excludeId: isEdit && packingList ? packingList._id : undefined },
      }).then((res) => {
        if (!cancelled && res.data && res.data.valid && res.data.available === false) setNumberError(t('mis.plNumberTaken', { number: Number(digits) }));
      }).catch(() => { /* the save will say it */ });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, numberTouched, docNumber, activeBranchId]);

  const shownNumber = (isEdit || numberTouched) ? docNumber : (suggested == null ? '' : String(suggested));
  const resetNumberToAutomatic = () => { setNumberError(''); setNumberTouched(false); setDocNumber(''); };
  const typeNumber = (raw) => {
    const digits = String(raw).replace(/\D/g, '').slice(0, 8);
    setNumberError('');
    // emptying the box of a NEW list hands the number back to the automatic one
    if (!isEdit && digits === '') { resetNumberToAutomatic(); return; }
    setNumberTouched(true);
    setDocNumber(digits);
  };
  const refuseNumber = (message) => {
    setNumberError(message);
    setTimeout(() => { if (numberRef.current) numberRef.current.focus(); }, 0);
  };

  // ── the unsent draft of a NEW list ───────────────────────────────────────────
  const draftable = !isEdit;
  const signature = JSON.stringify([
    type, selectedInvoices.map((i) => i._id), driverInfo, vehicleInfo, customsAgent, loadingOfficer,
    originAddress, destinationAddress, shippingDestination, standardThicknessCm, stoneNames, pallets, status, notes, issueDate,
    numberTouched ? docNumber : '',     // the suggestion arriving is not a change
  ]);
  // The form as it stood right after it was set up (blank, or seeded from a supply record).
  useEffect(() => {
    if (open && hydratedAt) setBaseline(signature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydratedAt]);
  // A draft being carried on counts as changed straight away: closing it must ask, not silently drop it.
  const dirty = open && draftable && (fromDraft || (baseline !== null && signature !== baseline));
  const draftTitle = [
    pallets.filter((p) => String(p.palletId || '').trim()).length
      ? t('mis.plPalletCount', { count: pallets.filter((p) => String(p.palletId || '').trim()).length }) : '',
    totals.sqm ? `${totals.sqm.toFixed(2)} ${t('mis.plColSqm')}` : '',
    driverInfo.fullName,
  ].filter(Boolean).join(' · ');
  const draftApi = useFormDraft({
    key: draftKey,
    kind: MIS_DRAFT.packingList.kind,
    enabled: open && draftable && Boolean(draftKey),
    dirty,
    snapshot: {
      type,
      // ids still being fetched stay in the draft: autosaving before they arrive must not lose them
      invoiceIds: restoringIds.length ? restoringIds : selectedInvoices.map((i) => i._id),
      driverInfo, vehicleInfo, customsAgent, loadingOfficer,
      originAddress, destinationAddress, shippingDestination, standardThicknessCm,
      stoneNames, pallets, status, notes, issueDate, dateSystem,
      docNumber: numberTouched ? docNumber : '', docNumberTouched: numberTouched,
      preset: effPreset ? {
        supplyRecordId: effPreset.supplyRecordId, recordTitle: effPreset.recordTitle, productCode: effPreset.productCode,
      } : null,
    },
    meta: { branchId: activeBranchId, title: draftTitle },
  });

  const handleClose = () => {
    if (saving) return;
    if (draftable && dirty) { setConfirmClose(true); return; }
    if (draftable) draftApi.clear();
    onClose();
  };

  const handleSave = async () => {
    if (type === 'linked' && selectedInvoices.length === 0) { setError(t('mis.plSelectInvoiceError')); return; }
    if (pallets.every((p) => !String(p.palletId || '').trim())) { setError(t('mis.plAddPalletError')); return; }
    if (outOfScope.length) { setError(t('mis.plOutOfScopeError', { codes: outOfScope.join(', ') })); return; }
    // Two pallets with the same reference (or the same ID) cannot be told apart: it has to be fixed first.
    if (clashes.groups.length) {
      setError(clashMessage(clashes.groups));
      setTimeout(() => { const bad = document.querySelector('[data-pallet-clash="true"] input'); if (bad) bad.focus(); }, 0);
      return;
    }
    // A number already flagged as used must be changed, not sent again.
    if (numberError) { refuseNumber(numberError); return; }

    // The number goes to the server only when it is the person's: typed on a new list, changed on an existing
    // one. Otherwise the server takes the next free automatic number at the moment of saving.
    let numberPart = {};
    if (isEdit) {
      if (docNumber.trim() === '') { refuseNumber(t('mis.plNumberRequired')); return; }
      if (Number(docNumber) < 1) { refuseNumber(t('mis.plNumberInvalid')); return; }
      if (Number(docNumber) !== Number(packingList.docNumber)) numberPart = { docNumber: Number(docNumber) };
    } else if (numberTouched) {
      if (Number(docNumber) < 1) { refuseNumber(t('mis.plNumberInvalid')); return; }
      numberPart = { docNumber: Number(docNumber) };
    }

    setSaving(true); setError('');
    try {
      const data = {
        ...numberPart,
        type,
        invoiceIds: type === 'linked' ? selectedInvoices.map((i) => i._id) : [],
        branchId: activeBranchId,
        issueDate, dateSystem,
        driverInfo, vehicleInfo, customsAgent, loadingOfficer,
        originAddress, destinationAddress, shippingDestination,
        standardThicknessCm: standardThicknessCm === '' ? undefined : Number(standardThicknessCm),
        // a picked stone goes as its product (the server re-reads it), a typed one as its name; always sent, so
        // emptying the field on an edit clears them
        stoneNames: stoneNames.map((s) => (s.productId ? { productId: s.productId, name: s.name } : { name: s.name })),
        pallets: pallets.filter((p) => String(p.palletId || '').trim()).map((p) => ({
          palletId: p.palletId, reference: p.reference,
          productCode: p.productCode, processingType: p.processingType,
          items: p.items.filter((it) => String(it.code || '').trim()).map((it) => ({
            code: it.code,
            lengthCm:    it.lengthCm    === '' ? undefined : Number(it.lengthCm),
            widthCm:     it.widthCm     === '' ? undefined : Number(it.widthCm),
            thicknessCm: it.thicknessCm === '' ? undefined : Number(it.thicknessCm),
            pcs:         it.pcs         === '' ? undefined : Number(it.pcs),
            sqm:         it.sqm         === '' ? undefined : Number(it.sqm),
          })),
        })),
        status,
        notes,
        // Raised from a Supply record — link it there (re-validated server-side
        // against this branch).
        ...(!isEdit && effPreset?.supplyRecordId ? { supplyRecordId: effPreset.supplyRecordId } : {}),
      };
      await dispatch(saveMisPackingList({ authCtx, axiosGlobal, id: packingList?._id, data })).unwrap();
      if (draftable) await draftApi.clear();      // it is a real list now
      onClose();
    } catch (err) {
      if (err && err.code === 'DOC_NUMBER_TAKEN') {
        const taken = t('mis.plNumberTaken', { number: err.docNumber != null ? err.docNumber : docNumber });
        refuseNumber(taken);
        setError(taken);
      } else if (err && err.code === 'DUPLICATE_PALLET' && Array.isArray(err.duplicates)) {
        setError(clashMessage(err.duplicates.map((d) => ({ field: d.field, value: d.value, pallets: d.palletIds }))));
      } else {
        setError((err && err.message) || t('mis.plSaveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  // ── shared field styling ──────────────────────────────────────────────────
  const field = { size: 'small', InputLabelProps: { shrink: true } };

  // Identifiers (IBAN, national ID, smart numbers, plates, phones) are always
  // latin digits read left-to-right, even when the UI is in Arabic or Farsi —
  // forcing dir=ltr stops them rendering reversed in an RTL layout.
  const idField = {
    ...field,
    inputProps: { dir: 'ltr', inputMode: 'numeric' },
    sx: { '& input': { fontFamily: 'monospace', fontSize: '0.8rem', letterSpacing: 0.3 } },
  };
  const telField = {
    ...field,
    type: 'tel',
    inputProps: { dir: 'ltr', inputMode: 'tel' },
    sx: { '& input': { fontFamily: 'monospace', fontSize: '0.8rem' } },
  };

  // The totals chips of the footer are larger on a phone.
  const footerChip = { height: isXs ? 26 : 22, fontSize: isXs ? '0.74rem' : '0.65rem' };

  return (
    <>
    <Drawer anchor="right" open={open} onClose={handleClose}
      PaperProps={{ sx: { width: isXs ? '100vw' : 760, maxWidth: '100vw', ...(isXs ? PHONE_FORM_SX : {}) } }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: { xs: 2, sm: 3 }, py: 2, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.TEXT_PRI }}>
            {isEdit ? t('mis.plEditTitle') : t('mis.plNewTitle')}
          </Typography>
          {isEdit && (
            <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC }}>#{packingList.docNumber}</Typography>
          )}
        </Box>
        {draftable && draftApi.savedAt && (
          <Typography data-testid="draft-saved" sx={{ fontSize: '0.64rem', color: T.TEXT_TER, ml: 'auto', mr: 1 }}>
            {t('mis.draftSaved', { time: new Date(draftApi.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
          </Typography>
        )}
        <IconButton onClick={handleClose} size="small" aria-label={t('common.close')} sx={isXs ? PHONE_BTN : undefined}>
          <CloseIcon sx={{ fontSize: isXs ? 22 : 18 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, flex: 1, overflowY: 'auto' }}>

        {!isEdit && effPreset?.supplyRecordId && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1.25, py: 1,
            borderRadius: '10px', border: `1px solid ${T.BD}`, bgcolor: T.CTRL_BG }}>
            <TerrainIcon sx={{ fontSize: 16, color: T.TEXT_SEC }} />
            <Typography sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>
              {t('mis.plLinkedToRecord', { record: effPreset.recordTitle || '—' })}
            </Typography>
          </Box>
        )}

        {/* ── Document: its number, its date and the calendar the date is written in ── */}
        <Section T={T} label={t('mis.sectionDocument')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: isXs ? '1fr' : '1fr 1fr', gap: 1.25, alignItems: 'start' }}>
            {/* The number the printed packing list carries. Filled in automatically; type another to change it. */}
            <TextField {...field} label={t('mis.plFieldNumber')} value={shownNumber} inputRef={numberRef}
              onChange={(e) => typeNumber(e.target.value)}
              placeholder={t('mis.plNumberAutomatic')}
              error={Boolean(numberError)}
              helperText={numberError || (isEdit ? t('mis.plNumberEditHelp')
                : numberTouched ? t('mis.plNumberCustomHelp') : t('mis.plNumberAutoHelp'))}
              inputProps={{ inputMode: 'numeric', dir: 'ltr', maxLength: 8, 'data-testid': 'pl-number' }}
              InputProps={{
                endAdornment: !isEdit && numberTouched ? (
                  <InputAdornment position="end">
                    <Tooltip title={t('mis.plNumberUseAuto')}>
                      <IconButton size="small" onClick={resetNumberToAutomatic} aria-label={t('mis.plNumberUseAuto')}>
                        <RestartAltIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ '& input': { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.5 } }} />
            <DateSystemSelect value={dateSystem} onChange={(v) => setDateSystem(v)} />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <MisDateField label={t('mis.plFieldDate')} system={dateSystem} value={issueDate}
                onChange={(iso) => setIssueDate(iso)} />
            </Box>
          </Box>
        </Section>

        {/* ── Scope ── */}
        <Section T={T} label={t('mis.plScopeSection')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            mb: type === 'linked' ? 1.5 : 0 }}>
            <ToggleButtonGroup size="small" exclusive value={type} disabled={isEdit}
              onChange={(_, v) => v && setType(v)}>
              <ToggleButton value="free" sx={{ fontSize: '0.72rem', textTransform: 'none', px: 2 }}>
                {t('mis.plTypeFree')}
              </ToggleButton>
              <ToggleButton value="linked" sx={{ fontSize: '0.72rem', textTransform: 'none', px: 2 }}>
                {t('mis.plTypeLinked')}
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Draft vs final — the list shows this as a status rail, so it has
                to be settable here. */}
            <ToggleButtonGroup size="small" exclusive value={status}
              onChange={(_, v) => v && setStatus(v)}>
              {[
                { v: 'draft', l: t('mis.plStatusDraft'), c: '#ffb74d' },
                { v: 'final', l: t('mis.plStatusFinal'), c: '#81c784' },
              ].map((o) => (
                <ToggleButton key={o.v} value={o.v}
                  sx={{ fontSize: '0.72rem', textTransform: 'none', px: 2, gap: 0.6,
                    '&.Mui-selected': { color: o.c } }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%',
                    bgcolor: status === o.v ? o.c : T.TEXT_TER }} />
                  {o.l}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {type === 'linked' && (
            <>
              <Autocomplete
                multiple options={invoiceOptions} value={selectedInvoices}
                onChange={(_, v) => setSelectedInvoices(v)}
                onInputChange={(_, v) => setInvoiceSearch(v)}
                getOptionLabel={(o) => `#${o.docNumber}${o.customerSnapshot?.name ? ' — ' + o.customerSnapshot.name : ''}`}
                isOptionEqualToValue={(o, v) => String(o._id) === String(v._id)}
                renderInput={(params) => (
                  <TextField {...params} {...field} label={t('mis.plLinkedInvoicesLabel')} />
                )}
              />
              <Typography sx={{ fontSize: '0.66rem', color: T.TEXT_TER, mt: 0.75 }}>
                {scope.prefixes.size > 0
                  ? t('mis.plScopeActive', { codes: [...scope.prefixes].join(', ') })
                  : t('mis.plScopeHelper')}
              </Typography>
            </>
          )}
        </Section>

        {/* ── Stone: one or more names, picked from Inventory or typed ── */}
        <Section T={T} label={t('mis.plStoneSection')}>
          <StoneNamesField value={stoneNames} onChange={setStoneNames} T={T} touch={isXs}
            branchId={isEdit ? packingList.branchId : activeBranchId} />
        </Section>

        {/* ── Driver & vehicle ── */}
        <Section T={T} label={t('mis.plDriverSection')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: isXs ? '1fr' : '1fr 1fr', gap: 1.25 }}>
            <TextField {...field} label={t('mis.driverFullNameField')} value={driverInfo.fullName}
              onChange={(e) => setDriverInfo((d) => ({ ...d, fullName: e.target.value }))} />
            <TextField {...telField} label={t('mis.driverPhoneField')} value={driverInfo.phone}
              onChange={(e) => setDriverInfo((d) => ({ ...d, phone: e.target.value }))} />
            <TextField {...idField} label={t('mis.driverNationalIdField')} value={driverInfo.nationalId}
              onChange={(e) => setDriverInfo((d) => ({ ...d, nationalId: e.target.value }))} />
            <TextField {...idField} label={t('mis.driverSmartNumberField')} value={driverInfo.smartNumber}
              onChange={(e) => setDriverInfo((d) => ({ ...d, smartNumber: e.target.value }))} />
            <TextField {...field} label={t('mis.trailerPlateField')} value={vehicleInfo.trailerPlateNumber}
              inputProps={{ dir: 'ltr' }}
              onChange={(e) => setVehicleInfo((v) => ({ ...v, trailerPlateNumber: e.target.value }))} />
            <TextField {...idField} label={t('mis.trailerSmartNumberField')} value={vehicleInfo.trailerSmartNumber}
              onChange={(e) => setVehicleInfo((v) => ({ ...v, trailerSmartNumber: e.target.value }))} />

            {/* IBAN is ~24-34 characters — it gets the full row, a monospace
                LTR input, and is shown grouped in fours the way a bank prints
                it. The stored value stays unspaced and uppercased. */}
            <TextField {...field} label={t('mis.driverIbanField')}
              value={ibanDisplay(driverInfo.iban)}
              onChange={(e) => setDriverInfo((d) => ({ ...d, iban: ibanRaw(e.target.value) }))}
              placeholder="IR00 0000 0000 0000 0000 0000 00"
              inputProps={{ dir: 'ltr', spellCheck: false, autoCapitalize: 'characters' }}
              helperText={driverInfo.iban
                ? t('mis.plIbanChars', { count: driverInfo.iban.length })
                : t('mis.plIbanHelp')}
              sx={{ gridColumn: isXs ? 'auto' : '1 / -1',
                '& input': { fontFamily: 'monospace', fontSize: '0.82rem', letterSpacing: 1 } }} />
          </Box>
        </Section>

        {/* ── Route & handlers ── */}
        <Section T={T} label={t('mis.plLogisticsSection')}>
          <Box sx={{ display: 'grid', gridTemplateColumns: isXs ? '1fr' : '1fr 1fr', gap: 1.25 }}>
            <TextField {...field} label={t('mis.loadingOfficerNameField')} value={loadingOfficer.name}
              onChange={(e) => setLoadingOfficer((o) => ({ ...o, name: e.target.value }))} />
            <TextField {...telField} label={t('mis.loadingOfficerPhoneField')} value={loadingOfficer.phone}
              onChange={(e) => setLoadingOfficer((o) => ({ ...o, phone: e.target.value }))} />
            <TextField {...field} label={t('mis.customsAgentNameField')} value={customsAgent.name}
              onChange={(e) => setCustomsAgent((c) => ({ ...c, name: e.target.value }))} />
            <TextField {...telField} label={t('mis.customsAgentPhoneField')} value={customsAgent.phone}
              onChange={(e) => setCustomsAgent((c) => ({ ...c, phone: e.target.value }))} />

            {/* Addresses run long — full row, and multiline so a wrapped
                address stays readable instead of scrolling inside one line. */}
            <TextField {...field} label={t('mis.originAddressField')} value={originAddress}
              multiline minRows={2} sx={{ gridColumn: isXs ? 'auto' : '1 / -1' }}
              onChange={(e) => setOriginAddress(e.target.value)} />
            <TextField {...field} label={t('mis.destinationAddressField')} value={destinationAddress}
              multiline minRows={2} sx={{ gridColumn: isXs ? 'auto' : '1 / -1' }}
              onChange={(e) => setDestinationAddress(e.target.value)} />

            <TextField {...field} label={t('mis.shippingDestinationField')} value={shippingDestination}
              helperText={t('mis.plShippingDestHelp')}
              onChange={(e) => setShippingDestination(e.target.value)} />
            <TextField {...field} type="number" label={t('mis.standardThicknessField')}
              value={standardThicknessCm} helperText={t('mis.plStandardThicknessHelp')}
              inputProps={{ inputMode: 'decimal', step: '0.1', min: 0 }}
              sx={{ '& input': { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
              onChange={(e) => applyStandardThickness(e.target.value)} />
          </Box>
        </Section>

        {/* ── Pallets ── */}
        <Section T={T} label={t('mis.plPalletsSection')}
          hint={t('mis.plTotalsSummary', { pcs: totals.pcs, sqm: totals.sqm.toFixed(2) })}>

          {outOfScope.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.72rem', py: 0.25 }}>
              {t('mis.plOutOfScopeWarning', { codes: outOfScope.join(', ') })}
            </Alert>
          )}
          {pallets.length > 1 && (
            <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_SEC, mb: 1 }}>{t('mis.plPalletOrderHelp')}</Typography>
          )}
          <Box role="status" aria-live="polite"
            sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
            {announce}
          </Box>

          <Box ref={flip.containerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {pallets.map((pallet, pIdx) => (
              <PalletCard key={pallet._key || pIdx} pallet={pallet} index={pIdx}
                isLast={pIdx === pallets.length - 1} canRemove={pallets.length > 1}
                isMoved={Boolean(movedKey) && movedKey === pallet._key}
                clashIdText={clashes.id[pIdx] ? clashes.id[pIdx].join(', ') : ''}
                clashRefText={clashes.ref[pIdx] ? clashes.ref[pIdx].join(', ') : ''}
                isXs={isXs} T={T} type={type} scopePrefixes={scope.prefixes} codeOptions={codeOptions}
                specOpen={specKey === pallet._key} canCreateVariety={canCreateVariety} searchProducts={searchForBuilder}
                onMoveBy={movePalletBy} onMoveTo={movePalletTo} onDuplicate={duplicatePallet} onRemove={removePallet}
                onField={setPalletField} onAddItem={addItem} onRemoveItem={removeItem} onItemField={setItemField}
                onResetSqm={resetSqm} onToggleSpec={toggleSpec} onUseBuilt={addBuiltItem} />
            ))}
          </Box>

          <Button size="small" variant={isXs ? 'outlined' : 'text'} startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={addPallet}
            sx={{ fontSize: '0.7rem', textTransform: 'none', mt: 1.25,
              ...(isXs ? { width: '100%', minHeight: 48, fontSize: '0.85rem', borderStyle: 'dashed' } : {}) }}>
            {t('mis.addPalletButton')}
          </Button>
        </Section>

        <TextField {...field} label={t('mis.sectionNotes')} value={notes} multiline minRows={2} fullWidth
          onChange={(e) => setNotes(e.target.value)} />

        {error && (
          <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.75rem', py: 0.25 }}>{error}</Alert>
        )}
      </Box>

      {/* Footer — running totals stay visible while saving. On a phone the totals take a line and the two buttons a second,
          full width and 48px tall, clear of the home-indicator bar. */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: { xs: 2, sm: 3 }, pt: 2,
        pb: { xs: 'max(16px, env(safe-area-inset-bottom))', sm: 2 }, borderTop: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip size="small" label={t('mis.plPalletCount', { count: pallets.filter((p) => String(p.palletId || '').trim()).length })}
            sx={footerChip} />
          <Chip size="small" label={`${totals.pcs} ${t('mis.plColPcs')}`} sx={footerChip} />
          <Chip size="small" label={`${totals.sqm.toFixed(2)} ${t('mis.plColSqm')}`} sx={footerChip} />
        </Box>
        <Box sx={{ marginInlineStart: { sm: 'auto' }, display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button onClick={handleClose} disabled={saving}
            sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none', minWidth: 90, flex: { xs: 2, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>
            {t('common.save')}
          </Button>
        </Box>
      </Box>
    </Drawer>

    {/* What to do with the unsent list on the way out. */}
    {draftable && (
      <DraftCloseDialog
        open={confirmClose}
        onCancel={() => setConfirmClose(false)}
        onKeep={async () => { await draftApi.flush(); setConfirmClose(false); onClose(); }}
        onDiscard={async () => { await draftApi.clear(); setConfirmClose(false); onClose(); }}
      />
    )}
    </>
  );
}
